import React from 'react'
import styled from '@emotion/styled'
import { withTranslation } from '~/i18n'
import shuffle from 'lodash/shuffle'
import { H2, Button, space, device, Avatar, useTransition, easing } from '@ticketswap/solar'
import { useLazyQuery } from '@apollo/client'
import TestimonialCard from '../TestimonialCard'
import { GET_TESTIMONIALS } from './getTestimonials'
import { GET_USERCOUNT_AND_AVATARS } from './getUsercountAndAvatars'
import { useLanguage } from '../LanguageContext'
import { Block } from '../Block'
import { WidePageLayout } from '../PageLayout'

const Center = styled.header`
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  padding-left: ${space[16]};
  padding-right: ${space[16]};

  @media ${device.laptop} {
    max-width: ${600 / 16}rem;
  }
`

const Container = styled.div`
  position: relative;
`

const Cards = styled(WidePageLayout)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${384 / 16}rem;
`

const duration = 400
const ItemContainer = styled.div`
  position: relative;
  width: 80%;
  opacity: ${props => (props.state === 'entering' || props.state === 'entered' ? 1 : 0)};
  transition-timing-function: ${easing.easeOutCubic};
  transition-property: opacity, transform;

  &:first-of-type {
    z-index: 1;
    transform: ${props => {
      switch (props.state) {
        case 'exited':
          return 'rotate(4deg) translate3d(2rem,0,0)'
        case 'exiting':
          return 'rotate(4deg) translate3d(-2rem,0,0)'
        default:
          return 'rotate(4deg) translate3d(0,0,0)'
      }
    }};
    transition-duration: ${duration - 300}ms;
    display: none;
  }

  &:nth-of-type(2) {
    z-index: 2;
    transform: ${props => {
      switch (props.state) {
        case 'exited':
          return 'rotate(-3deg) translate3d(2rem,0,0)'
        case 'exiting':
          return 'rotate(-3deg) translate3d(-2rem,0,0)'
        default:
          return 'rotate(-3deg) translate3d(0,0,0)'
      }
    }};
    transition-duration: ${duration - 150}ms;
    display: none;
  }

  &:last-of-type {
    z-index: 3;
    transform: ${props => {
      switch (props.state) {
        case 'exited':
          return 'rotate(4deg) translate3d(2rem,0,0)'
        case 'exiting':
          return 'rotate(4deg) translate3d(-2rem,0,0)'
        default:
          return 'rotate(4deg) translate3d(0,0,0)'
      }
    }};
    transition-duration: ${duration}ms;
    display: block;
  }

  @media ${device.laptop} {
    width: 40%;

    &:first-of-type {
      display: block;
    }
  }

  @media ${device.laptop} {
    width: calc(100% / 3);

    &:nth-of-type(2) {
      display: block;
    }
  }
`

const Background = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  padding-top: ${space[32]};
  padding-bottom: ${space[32]};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    z-index: 1;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 80%);

    [data-theme='dark'] & {
      background-image: radial-gradient(rgb(18 20 22) 0%, rgb(0 0 0 / 0%) 50%);
    }
  }
`

const Track = styled.div`
  display: flex;
  position: relative;

  &:nth-of-type(even) {
    left: -${space[64]};
  }
`

const AvatarContainer = styled.div`
  text-align: center;
  width: ${space[128]};
  flex-shrink: 0;
`

const Testimonials = ({ t }) => {
  let timer = null
  const language = useLanguage()
  const [fetchingMore, setFetchingMore] = React.useState(false)

  const queryOptions = {
    notifyOnNetworkStatusChange: true,
    variables: {
      first: 3,
      language: language,
      minimumRating: 3,
      minimumLength: 5,
      maximumLength: 200,
    },
  }
  const [getTestimonials, { loading, data = {}, fetchMore }] = useLazyQuery(
    GET_TESTIMONIALS,
    queryOptions
  )
  const [getUsercountAndAvatars, { data: usercountAndAvatarsData = {} }] = useLazyQuery(
    GET_USERCOUNT_AND_AVATARS
  )

  React.useEffect(() => {
    getTestimonials()
    getUsercountAndAvatars()
    return () => {
      clearTimeout(timer)
    }
  }, [getTestimonials, getUsercountAndAvatars, timer])

  const { testimonials = {} } = data
  const { randomUserAvatars = [], userCount } = usercountAndAvatarsData
  const { edges = [], pageInfo = {} } = testimonials

  const [state, mounted] = useTransition({
    in: !loading && !fetchingMore && edges.length > 0,
    timeout: duration,
  })

  const handleFetchMore = () => {
    setFetchingMore(true)
    timer = setTimeout(() => {
      fetchMore({
        query: GET_TESTIMONIALS,
        variables: { ...queryOptions.variables, after: pageInfo.endCursor },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          setFetchingMore(false)
          const { testimonials } = fetchMoreResult
          const newEdges = testimonials.edges
          const pageInfo = testimonials.pageInfo

          return newEdges.length
            ? {
                testimonials: {
                  ...previousResult.testimonials,
                  edges: newEdges,
                  pageInfo,
                },
              }
            : previousResult
        },
      })
    }, duration)
  }

  const alpha = React.useMemo(() => shuffle(randomUserAvatars), [randomUserAvatars])
  const beta = React.useMemo(() => shuffle(randomUserAvatars), [randomUserAvatars])
  const gamma = React.useMemo(() => shuffle(randomUserAvatars), [randomUserAvatars])

  return (
    <Block>
      {userCount ? (
        <Center as="header">
          <H2>
            {t('common:testimonials.title', {
              count: new Intl.NumberFormat(language).format(userCount),
            })}
          </H2>
        </Center>
      ) : null}

      <Container>
        {edges?.length > 0 && (
          <Cards>
            {edges.map(({ node }) =>
              mounted ? (
                <ItemContainer state={state} key={node.id}>
                  <TestimonialCard {...node} />
                </ItemContainer>
              ) : null
            )}
          </Cards>
        )}

        {randomUserAvatars.length > 0 && (
          <Background>
            <Track>
              {alpha.map((item, i) => (
                <AvatarContainer key={item.avatar + i}>
                  <Avatar src={item.avatar} size={64} />
                </AvatarContainer>
              ))}
            </Track>
            <Track>
              {beta.map((item, i) => (
                <AvatarContainer key={item.avatar + i}>
                  <Avatar src={item.avatar} size={64} />
                </AvatarContainer>
              ))}
            </Track>
            <Track>
              {gamma.map((item, i) => (
                <AvatarContainer key={item.avatar + i}>
                  <Avatar src={item.avatar} size={64} />
                </AvatarContainer>
              ))}
            </Track>
          </Background>
        )}
      </Container>

      {pageInfo.hasNextPage && (
        <Center as="footer">
          <Button
            variant="secondary"
            size="small"
            rounded
            onClick={handleFetchMore}
            loading={loading || fetchingMore}
            disabled={loading || fetchingMore}
          >
            {t('common:generic.showMore')}
          </Button>
        </Center>
      )}
    </Block>
  )
}

export default withTranslation('common')(Testimonials)
